import { useEffect, useRef, useCallback } from 'react';

export const CursorEffects = () => {
  const cursorDotRef = useRef(null);
  const cursorRingRef = useRef(null);
  const canvasRef = useRef(null);
  const mousePos = useRef({ x: -100, y: -100 });
  const trailPoints = useRef([]);
  const animFrameRef = useRef(null);

  const drawTrail = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Decay older points
    trailPoints.current = trailPoints.current
      .map(p => ({ ...p, alpha: p.alpha * 0.88 }))
      .filter(p => p.alpha > 0.01);

    trailPoints.current.forEach(point => {
      const gradient = ctx.createRadialGradient(
        point.x, point.y, 0,
        point.x, point.y, point.size
      );
      gradient.addColorStop(0, `rgba(212, 175, 55, ${point.alpha * 0.5})`);
      gradient.addColorStop(0.5, `rgba(212, 175, 55, ${point.alpha * 0.15})`);
      gradient.addColorStop(1, `rgba(212, 175, 55, 0)`);
      ctx.beginPath();
      ctx.arc(point.x, point.y, point.size, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
    });

    animFrameRef.current = requestAnimationFrame(drawTrail);
  }, []);

  useEffect(() => {
    // Check for reduced motion
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isTouchDevice = 'ontouchstart' in window;

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }

    const onResize = () => {
      if (canvas) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };
    window.addEventListener('resize', onResize);

    const onMouseMove = (e) => {
      const { clientX: x, clientY: y } = e;
      mousePos.current = { x, y };

      if (cursorDotRef.current) {
        cursorDotRef.current.style.transform = `translate(${x}px, ${y}px)`;
      }
      if (cursorRingRef.current) {
        cursorRingRef.current.style.transform = `translate(${x}px, ${y}px)`;
      }

      if (!prefersReduced && !isTouchDevice) {
        trailPoints.current.push({ x, y, alpha: 0.7, size: 12 });
        if (trailPoints.current.length > 30) trailPoints.current.shift();
      }
    };

    window.addEventListener('mousemove', onMouseMove);

    if (!prefersReduced && !isTouchDevice) {
      animFrameRef.current = requestAnimationFrame(drawTrail);
    }

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', onResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [drawTrail]);

  return (
    <>
      <canvas ref={canvasRef} className="cursor-trail-canvas" data-testid="gold-cursor-trail" aria-hidden="true" />
      <div ref={cursorDotRef} className="custom-cursor cursor-dot" aria-hidden="true" />
      <div ref={cursorRingRef} className="custom-cursor cursor-ring" aria-hidden="true" />
    </>
  );
};
